import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useCurrentUser } from '../../hooks/useCurrentUser'
import useApi from '../../hooks/useApi'
import { buildSocialLinks } from '../../lib/socialLinks'
import { charityAddress } from '../../lib/address'
import {
  charityAccountPath,
  charityPublicProfilePath,
} from '../../lib/charityProfileLink'
import { ensureAbsoluteUrl } from '../../utils/url'
import ProfileTemplate from './ProfileTemplate'
import Loading from '../../components/loading'
import DonationModal from '../../components/CivWallet/DonationModal'
import {
  CharityProfileLeftSidebar,
  CharityProfileRightSidebar,
} from '../../components/Profiles/CharityProfile'
import CharityProfileMain from '../../components/Profiles/CharityProfile/CharityProfileMain'

export default function CharityProfilePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { id: idParam } = useParams()
  const charityId = Number(idParam)
  const { currentUser } = useCurrentUser()
  const { getCharity, getUserCharityRegistrations } = useApi()

  const [charity, setCharity] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [isVerifiedAdmin, setIsVerifiedAdmin] = useState(false)
  const [showDonationModal, setShowDonationModal] = useState(false)

  useEffect(() => {
    if (!charityId || Number.isNaN(charityId)) {
      setNotFound(true)
      setIsLoading(false)
      return
    }

    let cancelled = false
    setNotFound(false)
    setIsLoading(true)

    const run = async () => {
      try {
        const charityResponse = await getCharity(charityId)
        if (cancelled) return

        if (charityResponse.status === 404 || charityResponse.error || !charityResponse.data) {
          setCharity(null)
          setNotFound(true)
          return
        }

        setCharity(charityResponse.data)

        if (currentUser?.id) {
          const { data: registrations } = await getUserCharityRegistrations()
          if (cancelled) return
          const canEdit = (registrations || []).some(
            reg =>
              reg.verified &&
              reg.status === 'Verified' &&
              Number(reg.charity?.id) === charityId,
          )
          setIsVerifiedAdmin(canEdit)
        } else {
          setIsVerifiedAdmin(false)
        }
      } catch (_err) {
        if (!cancelled) {
          setCharity(null)
          setNotFound(true)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [charityId, currentUser?.id, getCharity, getUserCharityRegistrations])

  const profilePath = charityPublicProfilePath(charityId)
  const profileUrl = profilePath
    ? `${window.location.origin}${profilePath}`
    : null

  const onShareClick = useCallback(() => {
    if (!profileUrl) return
    navigator.clipboard.writeText(profileUrl).then(
      () => {
        if (typeof window !== 'undefined' && window.alert) {
          window.alert(t('share.copied', 'Profile link copied to clipboard.'))
        }
      },
      () => {},
    )
  }, [profileUrl, t])

  const handleCivDonateClick = useCallback(() => {
    if (!currentUser?.id) {
      navigate('/login', { state: { from: location } })
      return
    }
    if (!currentUser?.wallet_id) {
      navigate('/settings')
      return
    }
    setShowDonationModal(true)
  }, [currentUser?.id, currentUser?.wallet_id, location, navigate])

  if (isLoading) {
    return <Loading show />
  }

  if (notFound || !charity) {
    return (
      <div className="container py-5">
        <p className="text-muted">
          {t('charities.profile.notFound', 'Charity not found.')}
        </p>
      </div>
    )
  }

  const locationStr = charityAddress(charity)
  const cityState = [charity.city, charity.state].filter(Boolean).join(', ')
  const socialLinks = buildSocialLinks(charity)
  const hasExternalDonate = !!ensureAbsoluteUrl(charity.external_donation_url)
  const editPath = charityAccountPath(charity.id)

  return (
    <>
      <ProfileTemplate
        profileType="charity"
        headerProps={{
          logoUrl: charity.logo_url || null,
          businessName: charity.organization_name,
          onEditPageClick:
            isVerifiedAdmin && editPath
              ? () => navigate(editPath)
              : undefined,
          socialLinks,
          onShareClick: profileUrl ? onShareClick : undefined,
        }}
        breadcrumbItems={[
          { label: t('profiles.breadcrumbHome', 'CR Homepage'), href: '/' },
          { label: t('charities.profile.breadcrumb', 'Charity') },
          { label: charity.organization_name },
        ]}
        leftSidebarContent={
          <CharityProfileLeftSidebar
            name={charity.organization_name}
            ein={charity.ein}
            alsoKnownAs={charity.also_known_as}
            location={locationStr || cityState}
            mission={charity.mission}
            websiteUrl={charity.website_url}
          />
        }
        mainContent={
          <CharityProfileMain
            charity={charity}
            locationLabel={cityState}
          />
        }
        rightSidebarContent={
          <CharityProfileRightSidebar
            externalDonationUrl={charity.external_donation_url}
            amazonWishlistUrl={charity.amazon_wishlist_url}
            onCivDonateClick={
              hasExternalDonate ? undefined : handleCivDonateClick
            }
          />
        }
      />
      {showDonationModal && currentUser?.wallet_id ? (
        <DonationModal
          show
          walletId={currentUser.wallet_id}
          onHide={() => setShowDonationModal(false)}
          onSave={() => setShowDonationModal(false)}
        />
      ) : null}
    </>
  )
}
